/**
 * Precision rules for the amounts persisted on a ReportingFacilityChemical.
 *
 * These live here rather than in encamp-aggregation because the reporting-unit
 * override converts amounts in `applyReportingUnitOverride` and has to normalise
 * them exactly the way aggregation normalises its own. Two copies of a
 * truncation rule that decides a filed figure would be a compliance defect
 * waiting to happen, so there is one definition and aggregation imports it.
 */

/** Matches the precision of the postgres `double precision` type. */
export const FLOATING_POINT_PRECISION = 15;

/**
 * Truncates a number to a maximum string character length.
 * Uses string slicing (not rounding) to avoid pushing values into higher thresholds.
 */
export function truncateToCharLength(value: number, maxChars: number): number {
  const str = String(value);
  // Scientific notation (e.g. "1e-7") can't be meaningfully sliced
  if (str.length <= maxChars || str.includes("e") || str.includes("E")) {
    return value;
  }
  // ast-grep-ignore: no-unsafe-number-parsing
  return Number(str.slice(0, maxChars));
}

export type AmountField =
  | "maxAmount"
  | "averageAmount"
  | "maxAmountLargestContainer"
  | "storageLocationMaxAmount";

type AmountPrecisionFn = (value: number, field: AmountField) => number;

const defaultAmountPrecision: AmountPrecisionFn = (value, field) => {
  const maxChars = field === "maxAmountLargestContainer" ? 13 : 15;
  return truncateToCharLength(value, maxChars);
};

const STATE_AMOUNT_PRECISION: Record<string, AmountPrecisionFn> = {
  CA: defaultAmountPrecision,
  KS: defaultAmountPrecision,
};

/**
 * Applies state-specific precision truncation to an amount value.
 * States without a precision config return the value unchanged.
 */
export function applyStatePrecision(
  value: number,
  field: AmountField,
  state: string | null | undefined
): number {
  if (!state) return value;
  const precisionFn = STATE_AMOUNT_PRECISION[state];
  if (!precisionFn) return value;
  return precisionFn(value, field);
}
